import { platformApiClient } from '@/services/platform-api-client';

// ── Provider types ────────────────────────────────────────────────────────────

export interface ProviderResponseDto {
  id: string;
  providerName: string;
  providerType: string;
  active: boolean;
  enabled: boolean;
  readable: boolean;
  writable: boolean;
  rootPath: string | null;
  publicUrl: string | null;
  totalObjects: number;
  totalStorage: number;
  freeSpace: number | null;
  usedSpace: number | null;
  filesystemSize: number | null;
  health: string;
  lastHealthCheck: string | null;
  description: string | null;
  driverVersion: string;
}

export interface FilesystemInfoDto {
  rootPath: string;
  filesystemFreeBytes: number | null;
  filesystemTotalBytes: number | null;
  filesystemUsedBytes: number | null;
  usagePercent: number | null;
  largestFile: { path: string; sizeBytes: number; name: string } | null;
  newestFile: { path: string; modifiedAt: string; name: string } | null;
  oldestFile: { path: string; modifiedAt: string; name: string } | null;
  objectsByModule: Record<string, number>;
  objectsByTenant: Record<string, number>;
}

export interface LocalTestResult {
  success: boolean;
  latencyMs: number;
  error?: string;
  checks: {
    directoryExists: boolean;
    writePermission: boolean;
    readPermission: boolean;
    deletePermission: boolean;
    availableSpaceBytes: number | null;
    totalSpaceBytes: number | null;
  };
}

export interface UpdateLocalProviderInput {
  rootPath?: string;
  publicUrl?: string;
  maxUploadMb?: number;
  tempPath?: string;
}

export interface UpdateLocalResult {
  provider: ProviderResponseDto;
  migrationStarted: boolean;
  migrationId: string | null;
}

// ── Migration types ──────────────────────────────────────────────────────────

export interface MigrationDto {
  id: string;
  status: string;
  sourcePath: string;
  destPath: string;
  totalObjects: number;
  migratedObjects: number;
  failedObjects: number;
  currentFile: string | null;
  percentComplete: number;
  estimatedRemainingSeconds: number | null;
  startedAt: string | null;
  completedAt: string | null;
  pausedAt: string | null;
  createdAt: string;
  error: string | null;
}

// ── Provider API ─────────────────────────────────────────────────────────────
// NOTE: platformApiClient response interceptor already unwraps { data: T } → T.
// These functions receive the unwrapped value directly in r.data.

export async function listStorageProviders(): Promise<ProviderResponseDto[]> {
  const r = await platformApiClient.get('/platform/storage/providers');
  const data = r.data as unknown;
  return Array.isArray(data) ? (data as ProviderResponseDto[]) : [];
}

export async function getLocalProvider(): Promise<ProviderResponseDto> {
  const r = await platformApiClient.get('/platform/storage/providers/local');
  return r.data as ProviderResponseDto;
}

export async function getLocalFilesystem(): Promise<FilesystemInfoDto> {
  const r = await platformApiClient.get('/platform/storage/providers/local/filesystem');
  return r.data as FilesystemInfoDto;
}

export async function testLocalProvider(): Promise<LocalTestResult> {
  const r = await platformApiClient.post('/platform/storage/providers/local/test');
  return r.data as LocalTestResult;
}

export async function updateLocalProvider(
  input: UpdateLocalProviderInput,
): Promise<UpdateLocalResult> {
  const r = await platformApiClient.patch('/platform/storage/providers/local', input);
  return r.data as UpdateLocalResult;
}

export async function activateLocalProvider(): Promise<ProviderResponseDto> {
  const r = await platformApiClient.post('/platform/storage/providers/local/activate');
  return r.data as ProviderResponseDto;
}

// ── Migration API ────────────────────────────────────────────────────────────

export async function listMigrations(providerId?: string): Promise<MigrationDto[]> {
  const r = await platformApiClient.get('/platform/storage/migrations', {
    params: providerId ? { providerId } : undefined,
  });
  const data = r.data as unknown;
  return Array.isArray(data) ? (data as MigrationDto[]) : [];
}

export async function getActiveMigration(providerId: string): Promise<MigrationDto | null> {
  const r = await platformApiClient.get('/platform/storage/migrations/active', {
    params: { providerId },
  });
  return (r.data as MigrationDto | null) ?? null;
}

export async function getMigration(id: string): Promise<MigrationDto> {
  const r = await platformApiClient.get(`/platform/storage/migrations/${id}`);
  return r.data as MigrationDto;
}

export async function pauseMigration(id: string): Promise<MigrationDto> {
  const r = await platformApiClient.post(`/platform/storage/migrations/${id}/pause`);
  return r.data as MigrationDto;
}

export async function resumeMigration(id: string): Promise<MigrationDto> {
  const r = await platformApiClient.post(`/platform/storage/migrations/${id}/resume`);
  return r.data as MigrationDto;
}

export async function cancelMigration(id: string): Promise<MigrationDto> {
  const r = await platformApiClient.post(`/platform/storage/migrations/${id}/cancel`);
  return r.data as MigrationDto;
}
